import { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";

export const isImage = (ct) => (ct || "").startsWith("image/");
export const isPdf = (ct) => (ct || "").includes("pdf");
export const canPreview = (ct) => isImage(ct) || isPdf(ct);
export const fmt = (iso) => {
  try { return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

const blobUrl = async (requestId, att, type) => {
  const res = await api.get(`/requests/${requestId}/attachments/${att.id}/download`, { responseType: "blob" });
  return window.URL.createObjectURL(new Blob([res.data], { type }));
};

export const useInternalNotes = (requestId) => {
  const [data, setData] = useState({ notes: [], attachments: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const load = useCallback(async () => {
    try { const { data: d } = await api.get(`/requests/${requestId}/internal`); setData(d); }
    finally { setLoading(false); }
  }, [requestId]);
  useEffect(() => { load(); }, [load]);

  const addNote = async (text) => {
    setSaving(true);
    try { await api.post(`/requests/${requestId}/notes`, { text }); await load(); return true; }
    catch (err) { toast.error("Errore", { description: formatApiErrorDetail(err.response?.data?.detail) }); return false; }
    finally { setSaving(false); }
  };
  const delNote = async (id) => { await api.delete(`/requests/${requestId}/notes/${id}`); await load(); };
  const delAtt = async (id) => { await api.delete(`/requests/${requestId}/attachments/${id}`); await load(); };

  const upload = async (file) => {
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try { await api.post(`/requests/${requestId}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Allegato caricato"); await load(); }
    catch (err) { toast.error("Upload non riuscito", { description: formatApiErrorDetail(err.response?.data?.detail) }); }
    finally { setUploading(false); }
  };

  const openPreview = async (att) => {
    setPreviewLoading(true);
    try { setPreview({ url: await blobUrl(requestId, att, isPdf(att.content_type) ? "application/pdf" : att.content_type), type: att.content_type, name: att.filename }); }
    catch { toast.error("Anteprima non disponibile"); }
    finally { setPreviewLoading(false); }
  };
  const closePreview = () => { if (preview?.url) window.URL.revokeObjectURL(preview.url); setPreview(null); };

  const download = async (att) => {
    try {
      const url = await blobUrl(requestId, att, att.content_type);
      const a = document.createElement("a");
      a.href = url; a.download = att.filename;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Download non riuscito"); }
  };

  return { data, loading, saving, uploading, preview, previewLoading, addNote, delNote, delAtt, upload, openPreview, closePreview, download };
};
